import React, { useCallback, useEffect, useRef, useState } from 'react';
import { SlIcon } from '@shoelace-style/shoelace/dist/react';

/**
 * Pencil-reveal content-writer picker, structurally parallel to
 * `DesignerAssignControl` — shared by the creatives table (desktop `<td>` and
 * mobile card) and by `DetailDrawer`'s Info tab.
 *
 * Deliberately self-contained: Tailwind utilities only (no `.scss`) and a
 * native `<select>` rather than react-select, because the drawer host is a
 * fixed `z-50` panel and a portalled menu fights with it. Busy state stays
 * local — the parent owns the API call, the toast and the row patch, and only
 * tells us success/failure through `onAssign`'s resolved value.
 *
 * Differences from `DesignerAssignControl`:
 *  - `writers` items are ALREADY option-shaped `{ value, label }` (see
 *    `fetchContentWriters` in `../api`), not `{ content_writer_id, name }`.
 *  - No `locked` prop — content-writer assignment has no locked state
 *    (`../config`'s `canAssign` is always `true`).
 *  - Carries an inline instructions textarea while editing, so the
 *    "Instructions" capability the old `AssignModal` provided isn't lost.
 *    `row` may be either the raw table row (`content_instructions`) or the
 *    drawer's adapted row (`instructions`) — both are read defensively so the
 *    control works from either caller without the caller having to normalise.
 */
const ContentWriterAssignControl = ({ row, writers, writersLoading = false, onAssign }) => {
  const [editing, setEditing] = useState(false);
  const [selected, setSelected] = useState('');
  const [instructions, setInstructions] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // The control is reused across rows (table virtualisation, drawer swaps) —
  // collapse the editor whenever the creative underneath it changes so a half
  // finished pick can never leak onto a different row.
  useEffect(() => {
    setEditing(false);
    setSelected('');
    setInstructions('');
  }, [row?.bsc_id]);

  const openEditor = useCallback(() => {
    setSelected(row?.content_writer_id ? String(row.content_writer_id) : '');
    setInstructions(row?.instructions ?? row?.content_instructions ?? '');
    setEditing(true);
  }, [row]);

  const cancelEditor = useCallback(() => {
    setEditing(false);
    setSelected('');
    setInstructions('');
  }, []);

  const submit = useCallback(async () => {
    if (submitting || !selected) return;
    const picked = (writers || []).find((w) => String(w?.value) === String(selected));
    if (!picked) return;
    setSubmitting(true);
    try {
      const ok = await onAssign?.(row, picked.value, picked.label, instructions);
      if (!mountedRef.current) return;
      // Only a strict `true` closes the reveal — on a failure we keep the
      // picker open with the selection intact so the user can just retry.
      if (ok === true) {
        setEditing(false);
        setSelected('');
        setInstructions('');
      }
    } finally {
      if (mountedRef.current) setSubmitting(false);
    }
  }, [submitting, selected, writers, onAssign, row, instructions]);

  if (!editing) {
    return (
      <div className="flex items-center gap-1.5">
        {row?.content_writer_name ? (
          <span className="max-w-[10rem] overflow-hidden text-ellipsis whitespace-nowrap text-xs text-slate-700">
            {row.content_writer_name}
          </span>
        ) : (
          <span className="whitespace-nowrap text-xs italic text-slate-400">Unassigned</span>
        )}
        <sl-tooltip content={row?.content_writer_name ? 'Change content writer' : 'Assign content writer'}>
          <sl-icon-button library="lucide" name="edit" onClick={openEditor}></sl-icon-button>
        </sl-tooltip>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex flex-wrap items-center gap-1.5">
        <select
          className="rounded-md border border-slate-300 bg-white px-2 py-1 text-xs text-slate-700 outline-none focus:border-sky-400 focus:ring-1 focus:ring-sky-200 disabled:opacity-60"
          value={selected}
          onChange={(e) => setSelected(e.target.value)}
          disabled={submitting || writersLoading}
        >
          <option value="">Select content writer</option>
          {(writers || []).map((w) => (
            <option key={w.value} value={String(w.value)}>
              {w.label}
            </option>
          ))}
        </select>

        <button
          type="button"
          aria-label="Save content writer"
          onClick={submit}
          disabled={submitting || !selected || writersLoading}
          className="inline-flex h-6 w-6 items-center justify-center rounded-md bg-emerald-600 text-white transition hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {submitting ? (
            <span className="h-3 w-3 animate-spin rounded-full border-2 border-white/40 border-t-white" />
          ) : (
            <SlIcon library="lucide" name="check" style={{ fontSize: '13px' }} />
          )}
        </button>

        <button
          type="button"
          aria-label="Cancel"
          onClick={cancelEditor}
          disabled={submitting}
          className="inline-flex h-6 w-6 items-center justify-center rounded-md bg-slate-100 text-slate-500 transition hover:bg-slate-200 hover:text-slate-700 disabled:cursor-not-allowed disabled:opacity-50"
        >
          <SlIcon library="lucide" name="x" style={{ fontSize: '13px' }} />
        </button>
      </div>

      <textarea
        rows={2}
        value={instructions}
        disabled={submitting}
        onChange={(e) => setInstructions(e.target.value)}
        placeholder="Instructions for the writer (optional)"
        className="w-full min-w-[12rem] resize-y rounded-md border border-slate-300 bg-white px-2 py-1 text-xs text-slate-700 outline-none transition placeholder:text-slate-400 focus:border-sky-400 focus:ring-1 focus:ring-sky-200 disabled:opacity-60"
      />
    </div>
  );
};

export default ContentWriterAssignControl;
